import { Button, Group, Modal, Stack, Text, TextInput } from "@mantine/core";
import { useState } from "react";
import { useSaveSeason, type Season, type SeasonFields } from "./services-queries";

export interface SeasonModalProps {
  /** null to add a season, a season to edit it; undefined when closed. */
  readonly season: Season | null | undefined;
  readonly onClose: () => void;
  readonly onSaved?: (seasonId: string) => void;
}

/** A season is a named window: the unit of asking (ADR 0043 §2). */
export function SeasonModal({ season, onClose, onSaved }: SeasonModalProps) {
  const save = useSaveSeason();
  const [fields, setFields] = useState<SeasonFields>(
    season === null || season === undefined
      ? { name: "", startsOn: "", endsOn: "" }
      : { name: season.name, startsOn: season.startsOn, endsOn: season.endsOn },
  );
  const code = (save.error as { code?: string } | null)?.code;
  const errorText =
    code === "23505"
      ? "Another season already has that name. Archived seasons keep theirs."
      : code === "23514"
        ? "A season ends after it starts."
        : save.isError
          ? "The season was not saved. Check your access and try again."
          : null;
  const ready =
    fields.name.trim() !== "" && fields.startsOn !== "" && fields.endsOn >= fields.startsOn;
  return (
    <Modal
      opened={season !== undefined}
      onClose={onClose}
      title={season === null ? "Add a season" : "Edit season"}
      radius="lg"
    >
      <Stack gap="sm">
        <TextInput
          label="Name"
          placeholder="Autumn 2026"
          required
          value={fields.name}
          onChange={(e) => {
            const name = e.currentTarget.value;
            setFields((c) => ({ ...c, name }));
          }}
        />
        <Group grow>
          <TextInput
            label="From"
            type="date"
            required
            value={fields.startsOn}
            onChange={(e) => {
              const startsOn = e.currentTarget.value;
              setFields((c) => ({ ...c, startsOn }));
            }}
          />
          <TextInput
            label="To"
            type="date"
            required
            value={fields.endsOn}
            onChange={(e) => {
              const endsOn = e.currentTarget.value;
              setFields((c) => ({ ...c, endsOn }));
            }}
          />
        </Group>
        <Text size="xs" c="dimmed">
          Every volunteer gets one request for the whole season, however many dates they are on.
        </Text>
        {errorText !== null ? (
          <Text size="sm" c="red">
            {errorText}
          </Text>
        ) : null}
        <Group justify="flex-end" gap="sm" mt="xs">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!ready}
            loading={save.isPending}
            onClick={() => {
              save.mutate(
                { seasonId: season === null || season === undefined ? null : season.id, fields },
                {
                  onSuccess: (id) => {
                    if (onSaved !== undefined) onSaved(id);
                    onClose();
                  },
                },
              );
            }}
          >
            {season === null ? "Add season" : "Save season"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
